import apiClient from './apiClient';
import type { ApiResponse } from './insuranceService';

export type ClaimStatus = 'draft' | 'submitted' | 'accepted' | 'partially_accepted' | 'rejected' | 'cancelled';

export interface ClaimItem {
    id: number;
    ars_claim_id: number;
    claimable_type: string | null;
    claimable_id: number | null;
    description: string;
    quantity: number;
    unit_price: number;
    claimed_amount: number;
    authorized_amount: number | null;
    gloss_code: string | null;
    gloss_description: string | null;
    created_at: string;
    updated_at: string;
}

export interface ArsGloss {
    id: number;
    ars_claim_id: number;
    ars_claim_item_id: number | null;
    gloss_code: string;
    gloss_description: string;
    disputed_amount: number | null;
    status: 'pending' | 'disputed' | 'resolved';
    resolution_notes: string | null;
    created_at: string;
    updated_at: string;
}

export interface ArsClaimPayment {
    id: number;
    ars_claim_id: number;
    payment_date: string;
    amount: number;
    payment_reference: string;
    notes: string | null;
    created_at: string;
    updated_at: string;
}

export interface ArsClaim {
    id: number;
    claim_number: string;
    patient_id: number;
    insurance_company_id: number;
    insurance_plan_id: number;
    total_amount: number;
    claimed_amount: number;
    authorized_amount: number | null;
    status: ClaimStatus;
    submission_date: string | null;
    response_date: string | null;
    notes: string | null;
    created_at: string;
    updated_at: string;
    patient?: {
        id: number;
        first_name: string;
        last_name: string;
        identification: string;
    };
    insurance_company?: {
        id: number;
        name: string;
        code: string;
    };
    insurance_plan?: {
        id: number;
        name: string;
    };
    items?: ClaimItem[];
    glosses?: ArsGloss[];
    payments?: ArsClaimPayment[];
}

export interface ClaimFilters {
    page?: number;
    per_page?: number;
    status?: ClaimStatus | '';
    company_id?: number;
    patient_id?: number;
    search?: string;
    date_from?: string;
    date_to?: string;
}

export interface PaginatedResponse<T> {
    data: T[];
    total: number;
    per_page: number;
    current_page: number;
    last_page?: number;
}

export interface PaymentReconciliation {
    claimed_amount: number;
    total_paid: number;
    remaining_balance: number;
    status: 'unpaid' | 'partial' | 'paid';
    discrepancy_warning?: string;
}

export interface SisalrilResult {
    content: string;
    filename: string;
    claim_id?: number;
    claims_count?: number;
}

export const arsClaimService = {
    async getAll(filters: ClaimFilters = {}): Promise<PaginatedResponse<ArsClaim>> {
        const queryParams = new URLSearchParams();
        if (filters.page) queryParams.append('page', filters.page.toString());
        if (filters.per_page) queryParams.append('per_page', filters.per_page.toString());
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.company_id) queryParams.append('company_id', filters.company_id.toString());
        if (filters.patient_id) queryParams.append('patient_id', filters.patient_id.toString());
        if (filters.search) queryParams.append('search', filters.search);
        if (filters.date_from) queryParams.append('date_from', filters.date_from);
        if (filters.date_to) queryParams.append('date_to', filters.date_to);

        const response = await apiClient.get<PaginatedResponse<ArsClaim>>(`/ars/claims?${queryParams.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<ApiResponse<ArsClaim>> {
        const response = await apiClient.get<ApiResponse<ArsClaim>>(`/ars/claims/${id}`);
        return response.data;
    },

    async create(data: {
        patient_id: number;
        insurance_company_id: number;
        insurance_plan_id: number;
        items: Array<{
            description: string;
            quantity: number;
            unit_price: number;
            claimable_type?: string;
            claimable_id?: number;
        }>;
        notes?: string;
    }): Promise<ApiResponse<ArsClaim>> {
        const response = await apiClient.post<ApiResponse<ArsClaim>>('/ars/claims', data);
        return response.data;
    },

    async submitClaim(id: number): Promise<ApiResponse<ArsClaim>> {
        const response = await apiClient.post<ApiResponse<ArsClaim>>(`/ars/claims/${id}/submit`);
        return response.data;
    },

    async processResponse(id: number, data: {
        status: 'accepted' | 'partially_accepted' | 'rejected';
        glosses?: Array<{
            claim_item_id?: number;
            gloss_code: string;
            gloss_description: string;
            disputed_amount?: number;
        }>;
        authorized_amount?: number;
    }): Promise<ApiResponse<ArsClaim>> {
        const response = await apiClient.post<ApiResponse<ArsClaim>>(`/ars/claims/${id}/process-response`, data);
        return response.data;
    },

    async generateSisalril(id: number): Promise<ApiResponse<SisalrilResult>> {
        const response = await apiClient.get<ApiResponse<SisalrilResult>>(`/ars/claims/${id}/sisalril`);
        return response.data;
    },

    async generateSisalrilBatch(claimIds: number[]): Promise<ApiResponse<SisalrilResult>> {
        const response = await apiClient.post<ApiResponse<SisalrilResult>>('/ars/claims/sisalril/batch', { claim_ids: claimIds });
        return response.data;
    },

    async getGlosses(claimId: number): Promise<ApiResponse<ArsGloss[]>> {
        const response = await apiClient.get<ApiResponse<ArsGloss[]>>(`/ars/claims/${claimId}/glosses`);
        return response.data;
    },

    async disputeGloss(claimId: number, glossId: number, data: {
        disputed_amount: number;
        resolution_notes: string;
    }): Promise<ApiResponse<ArsGloss>> {
        const response = await apiClient.post<ApiResponse<ArsGloss>>(`/ars/claims/${claimId}/glosses/${glossId}/dispute`, data);
        return response.data;
    },

    async acceptGloss(claimId: number, glossId: number, resolutionNotes?: string): Promise<ApiResponse<ArsGloss>> {
        const response = await apiClient.post<ApiResponse<ArsGloss>>(`/ars/claims/${claimId}/glosses/${glossId}/accept`, {
            resolution_notes: resolutionNotes,
        });
        return response.data;
    },

    async getPayments(claimId: number): Promise<ApiResponse<{
        payments: ArsClaimPayment[];
        reconciliation: PaymentReconciliation;
    }>> {
        const response = await apiClient.get<ApiResponse<{ payments: ArsClaimPayment[]; reconciliation: PaymentReconciliation }>>(`/ars/claims/${claimId}/payments`);
        return response.data;
    },

    async recordPayment(claimId: number, data: {
        amount: number;
        payment_reference: string;
        payment_date?: string;
        notes?: string;
    }): Promise<ApiResponse<{
        payment: ArsClaimPayment;
        reconciliation: PaymentReconciliation;
    }>> {
        const response = await apiClient.post<ApiResponse<{ payment: ArsClaimPayment; reconciliation: PaymentReconciliation }>>(`/ars/claims/${claimId}/payments`, data);
        return response.data;
    },
};

export default arsClaimService;
